import type { HierarchyNode } from "./types";

/**
 * Helper to recursively find a node by ID in the tree
 */
export function findNodeById<T extends HierarchyNode>(nodes: T[], id: string): T | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children && node.children.length > 0) {
      const found = findNodeById(node.children as T[], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Convert height values to proper CSS format
 */
export function formatHeight(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

/**
 * Validate data structure (dev only)
 */
export function validateHierarchyData<T extends HierarchyNode>(data: T[], rootNodeId?: string): void {
  if (process.env.NODE_ENV !== "development") return;

  const allIds = new Set<string>();
  const checkIds = (nodes: T[], path = "root") => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const nodePath = `${path}[${i}]`;

      // Warn about missing IDs
      if (!node.id) {
        console.warn(
          `[HierarchyExplorer] Node at ${nodePath} is missing an "id" field. Node name: "${node.name || "(unnamed)"}"`
        );
      } else if (allIds.has(node.id)) {
        console.warn(`[HierarchyExplorer] Duplicate node ID found: "${node.id}". Each node must have a unique ID.`);
      } else {
        allIds.add(node.id);
      }

      if (node.children) {
        checkIds(node.children as T[], `${nodePath}.children`);
      }
    }
  };
  checkIds(data);

  // Check if rootNodeId exists
  if (rootNodeId && !allIds.has(rootNodeId)) {
    console.warn(
      `[HierarchyExplorer] Root node ID "${rootNodeId}" not found in data. No node will be highlighted as root.`
    );
  }
}
